/**
 * Snapshots leves da fila antes de replay crítico + rollback.
 */

import type { QueueItem, QueueStatus } from '../queue/types';
import type { ReplayCheckpoint } from './persistentReplay';
import {
  getLastAckedSequenceSync,
  getLastReplayedSequenceSync,
  restoreCheckpointSnapshot,
} from './persistentReplay';
import { rebuildQueueIndexFromArray, validateQueueIndex } from '../queue/queueIndex';
import { recordReplayRolledBack } from '../telemetry';

export interface ReplayItemSnapshot {
  id: string;
  status: QueueStatus;
  sessionId: string;
  sessionSequence: number;
}

export interface ReplaySnapshot {
  id: string;
  capturedAt: number;
  items: ReplayItemSnapshot[];
  checkpoints: Record<string, Pick<ReplayCheckpoint, 'lastAckedSequence' | 'lastReplayedSequence'>>;
}

const snapshots = new Map<string, ReplaySnapshot>();
let latestId: string | null = null;

export function captureReplaySnapshot(queue: QueueItem[]): string {
  const id = `rs_${Date.now()}`;
  const sessionIds = new Set(queue.map(i => i.params.sessionId));
  const checkpoints: ReplaySnapshot['checkpoints'] = {};
  for (const sid of sessionIds) {
    checkpoints[sid] = {
      lastAckedSequence: getLastAckedSequenceSync(sid),
      lastReplayedSequence: getLastReplayedSequenceSync(sid),
    };
  }
  const snap: ReplaySnapshot = {
    id,
    capturedAt: Date.now(),
    items: queue.map(i => ({
      id: i.id,
      status: i.status,
      sessionId: i.params.sessionId,
      sessionSequence: i.sessionSequence,
    })),
    checkpoints,
  };
  snapshots.set(id, snap);
  latestId = id;
  return id;
}

export function rollbackReplaySnapshot(
  snapshotId: string,
  queue: QueueItem[]
): number {
  const snap = snapshots.get(snapshotId);
  if (!snap) return 0;
  const byId = new Map(snap.items.map(i => [i.id, i]));
  let restored = 0;
  for (const item of queue) {
    const prev = byId.get(item.id);
    if (!prev) continue;
    if (item.status === 'processing') {
      item.status = prev.status === 'processing' ? 'queued' : prev.status;
      delete item.processingAt;
      delete item.processingTabId;
      restored++;
    }
  }
  for (const [sessionId, cp] of Object.entries(snap.checkpoints)) {
    restoreCheckpointSnapshot(
      sessionId,
      cp.lastAckedSequence,
      cp.lastReplayedSequence
    );
  }
  const v = validateQueueIndex(queue);
  if (!v.ok) rebuildQueueIndexFromArray(queue);
  recordReplayRolledBack();
  snapshots.delete(snapshotId);
  if (latestId === snapshotId) latestId = null;
  return restored;
}

export function getLatestReplaySnapshotId(): string | null {
  return latestId;
}

export function _testClearReplaySnapshots(): void {
  snapshots.clear();
  latestId = null;
}
